// §branded-refill (docs/FE_ACK_BRANDED_REFILL_FOLLOWUP_2026_10_07.md; spec in the
// dashboard repo, memory/INTAKE_PRD_BRANDED_REFILL_FOLLOWUP_2026_10_07.md §7).
//
// A refill follow-up on a BRANDED prescription (Wegovy / Ozempic / Zepbound /
// Mounjaro / Foundayo — dispenseType 'regular_rx') charges the consult fee only:
// the patient's own pharmacy fills and bills the medication. The backend prices
// this through GET /followup/quote and the quote is bound to the payment call
// and to the submit, so the intake never computes a dollar amount.
//
// Everything in here is pure so the branching can be tested without React:
// which steps to inject, what the pay/submit bodies carry, how the dose
// request reaches the prescriber (ONLY through an answers Q/A pair — the
// structured `dose` block is the dashboard's copy, not what Beluga's clinician
// reads), and which backend rejections mean what.
//
// Nothing here runs unless the dashboard launched the follow-up with an Rx
// reference (rxId/medId) AND the backend answered the quote. A 404 (flag off)
// or any failure → `quote` stays null → today's flow, byte for byte.

export const REFILL_PHARMACY_ID = 'refill-pharmacy';
export const REFILL_DOSE_ID = 'refill-dose';
export const REFILL_SLIDE_IDS = new Set([REFILL_PHARMACY_ID, REFILL_DOSE_ID]);

export const ROUTE_LOCAL = 'local_pharmacy';
export const ROUTE_PARTNER = 'beluga_partner';
// BE #345: the drug maker's own pharmacy (NovoCare for Wegovy, LillyDirect
// for Zepbound). Consult-only like the local leg; the patient pays the
// manufacturer for the drug. Offered only when the quote lists the route AND
// carries the verified pharmacy (ops sets the id in SSM) — dark otherwise.
export const ROUTE_MANUFACTURER = 'manufacturer_pharmacy';

// Entry params the dashboard passes (all optional). `get` is
// `(key) => string | null`, i.e. URLSearchParams#get, so this reads the same
// in the form and in tests.
export function readRefillParams(get) {
  const g = (k) => {
    const v = get(k);
    return v === null || v === undefined || v === '' ? null : String(v);
  };
  return {
    rxId: g('rxId'),
    rxRowId: g('rxRowId'),
    medId: g('medId'),
    quoteId: g('quoteId'),
    visitType: g('visitType'),
    fulfillmentRoute: g('fulfillmentRoute'),
    pharmacyId: g('pharmacyId'),
    pricingBasis: g('pricingBasis'),
    product: g('product'),
  };
}

// Which launches ask for a quote. Originally only one that named the Rx
// (rxId / medId). Live issue 2026-10-07 (patient 141): the dashboard's Refill
// launched with masterId but NO medId — her Rx came from Beluga with medId
// null — so the intake skipped the quote and ran the compounded $199 flow for
// a branded tablet. The BE (#337/#339) now quotes the visit's LATEST Rx from
// masterId alone and resolves the medId from the catalog, so any dashboard
// launch (embed + masterId) asks. A 404 / flag-off still means today's flow.
// A direct, non-embed visit without an Rx reference keeps today's flow.
export function wantsQuote(params, { embed = false, masterId = null } = {}) {
  if (!params) return false;
  if (params.rxId || params.rxRowId || params.medId) return true;
  return Boolean(embed && masterId);
}

// The quote can say which follow-up questionnaire the Rx belongs to
// (`followupIntakeType`, e.g. 'brandedWeightloss'). It wins over the URL's
// ?type= and over patientPreference auto-detection, because those are what
// put a branded-tablet patient into the compounded questionnaire. Only values
// the form has a questionnaire for are honoured.
const QUOTE_GOAL_TYPES = new Set(['brandedWeightloss', 'antiAging', 'metabolic', 'trt', 'womenSexualHealth', 'mensSexualHealth', 'weightloss']);
export function goalTypeFromQuote(quote) {
  const t = quote && quote.followupIntakeType;
  return t && QUOTE_GOAL_TYPES.has(t) ? t : null;
}

export const isRegularRx = (quote) => Boolean(quote) && quote.dispenseType === 'regular_rx';
export const isConsultOnly = (quote) => Boolean(quote) && quote.pricingBasis === 'consult_only';
export const isLocalLeg = (quote) => Boolean(quote) && quote.fulfillmentRoute === ROUTE_LOCAL;
export const isManufacturerLeg = (quote) => Boolean(quote) && quote.fulfillmentRoute === ROUTE_MANUFACTURER;
// Local and manufacturer legs are both "the doctor e-prescribes elsewhere,
// Fitpal charges the consult": no Ship-to, prescriptionFulfillment 'local'.
export const isConsultLeg = (quote) => isLocalLeg(quote) || isManufacturerLeg(quote);

// { key: 'novocare' | 'lillydirect', name, pharmacyId } or null.
export function manufacturerOption(quote) {
  if (!quote || !allowedRoutes(quote).includes(ROUTE_MANUFACTURER)) return null;
  const m = quote.manufacturerPharmacy;
  return m && m.pharmacyId && m.name ? m : null;
}

const MANUFACTURER_BRAND = { novocare: 'Wegovy', lillydirect: 'Zepbound' };
export function manufacturerBrand(quote) {
  const m = manufacturerOption(quote);
  if (!m) return '';
  const b = quote.product && quote.product.brand;
  if (b && b !== 'Other') return b;
  return MANUFACTURER_BRAND[String(m.key || '').toLowerCase()] || '';
}

export function allowedRoutes(quote) {
  return Array.isArray(quote?.allowedRoutes) ? quote.allowedRoutes : [];
}

// Quotes live 30 minutes server-side; re-quote rather than pay with a dead one.
export function quoteExpired(quote, now = Date.now()) {
  if (!quote || !quote.expiresAt) return false;
  const t = Date.parse(quote.expiresAt);
  return Number.isFinite(t) && t <= now;
}

export function strengths(quote) {
  return Array.isArray(quote?.strengths) ? quote.strengths : [];
}

// The medId of the prescription being refilled. After a dose re-quote
// (`requestedMedId`) the backend returns `quote.medId` = the REQUESTED
// strength (that is what the payment must carry), while `product` /
// `rxName` / `rxStrength` keep describing the current Rx. The hook stamps the
// first quote's medId onto later ones as `rxMedId` so the "current dose" never
// drifts to the requested one (live checkout copy bug, 2026-10-07).
export function rxMedId(quote) {
  return (quote && (quote.rxMedId || quote.medId)) || null;
}

export function currentStrength(quote) {
  const id = rxMedId(quote);
  return strengths(quote).find((s) => s && s.medId === id) || null;
}

// What the patient is refilling, for display: the product name the backend
// derives from the Rx ("Wegovy 1.5mg tablet"), else rxName + rxStrength.
export function rxDisplayName(quote) {
  if (!quote) return 'your medication';
  const p = quote.product || {};
  if (p.name) return p.name;
  const rx = [quote.rxName, quote.rxStrength].filter(Boolean).join(' ');
  return rx || productName(quote);
}

// The numeric dose in a strength label ("2.5 mg", "Foundayo 14.5mg") — null when
// there is none. strengths[] comes back in CATALOG order, not dose order
// (BE, 2026-10-07: 0.8, 5.5, 2.5, 14.5, 9, 17.2 mg), so the picker sorts here.
export function strengthMg(label) {
  const m = /(\d+(?:\.\d+)?)\s*mg\b/i.exec(String(label || ''));
  return m ? Number(m[1]) : null;
}

// Strengths the patient may ASK for: live rows other than the one they are on,
// in ascending dose order (rows with no parsable dose keep catalog order, last).
export function alternativeStrengths(quote) {
  if (!quote) return [];
  const current = rxMedId(quote);
  return strengths(quote)
    .filter((s) => s && s.live !== false && s.medId && s.medId !== current)
    .map((s, i) => ({ s, i, mg: strengthMg(s.label) }))
    .sort((a, b) => {
      if (a.mg === null && b.mg === null) return a.i - b.i;
      if (a.mg === null) return 1;
      if (b.mg === null) return -1;
      return a.mg - b.mg || a.i - b.i;
    })
    .map((x) => x.s);
}

// "Where should we send your prescription?" — only on the local leg of a
// branded Rx. beluga_partner ships from the partner pharmacy as today (no
// step). A quote that allows only the partner route has nothing to ask.
export function needsPharmacyStep(quote) {
  if (!isRegularRx(quote)) return false;
  const routes = allowedRoutes(quote);
  if (!routes.includes(ROUTE_LOCAL) && !manufacturerOption(quote)) return false;
  return quote.fulfillmentRoute !== ROUTE_PARTNER;
}

// "Same pharmacy" is only offered when the Rx row carries a current pharmacy
// AND the current leg is already local (a legacy Rx with no route has no
// "same" to offer — §5 of the PRD).
export function hasSamePharmacyOption(quote) {
  if (!isConsultLeg(quote)) return false;
  const cur = quote.currentPharmacy && quote.currentPharmacy.pharmacyId;
  if (!cur) return false;
  // If the "current" pharmacy IS the manufacturer's, the manufacturer card is
  // that option — don't show it twice.
  const m = manufacturerOption(quote);
  if (m && String(m.pharmacyId) === String(cur)) return false;
  return true;
}

// Other FORMS of the same drug / dispense type (BE #341): e.g. a testosterone
// cream patient may ask for the cypionate injection. Priced and routed as
// their own catalog item on re-quote (quote.medId = alt, rxMedId = the Rx).
export function alternatives(quote) {
  if (!quote || !Array.isArray(quote.alternatives)) return [];
  const current = rxMedId(quote);
  return quote.alternatives.filter((a) => a && a.medId && a.medId !== current);
}

// What the patient asked for at INTAKE when it differs from what was
// prescribed (BE #341): { medId, name, strength, inCatalog, option }, option ∈
// 'current' | 'strength' | 'alternative' | null. Only the two "it's something
// else you could ask for" cases are surfaced.
export function requestedAtIntake(quote) {
  const r = quote && quote.requestedAtIntake;
  if (!r || !r.medId || r.inCatalog === false) return null;
  if (r.option !== 'strength' && r.option !== 'alternative') return null;
  if (r.medId === rxMedId(quote)) return null;
  return r;
}

// Clean catalog label (BE #342 `label`), else the name alone — never name +
// strength concatenated (that read badly: "Testosterone Cypionate 200MG/ML -
// 1ml 200MG/ML - 1 ml ~ Supplies per Month").
export function requestedAtIntakeLabel(r) {
  if (!r) return '';
  return r.label || r.name || r.medId || '';
}

// ── What this quote ORDERS vs what was PRESCRIBED ────────────────────────────
// After a pick the quote is re-fetched with requestedMedId and from then on
// quote.medId / totalCents / medicationCents describe the pick. BE #342 adds
// `ordered` {medId,label,…,isChange} and `prescribed` {medId,label,…} and makes
// `product` follow the ordered item; before #342 `product` is the prescribed
// item, so the pick's label has to come from the patient's answer.
export function isChange(quote) {
  if (!quote) return false;
  if (quote.ordered && typeof quote.ordered.isChange === 'boolean') return quote.ordered.isChange;
  return Boolean(quote.medId) && quote.medId !== rxMedId(quote);
}

export function orderedLabel(quote, doseAnswer) {
  if (!quote) return 'your medication';
  if (quote.ordered && quote.ordered.label) return quote.ordered.label;
  if (isChange(quote)) {
    if (doseAnswer && doseAnswer.requestedMedId === quote.medId && doseAnswer.requestedLabel) return doseAnswer.requestedLabel;
    const s = strengths(quote).find((x) => x && x.medId === quote.medId);
    if (s && s.label) return s.label;
    const a = (Array.isArray(quote.alternatives) ? quote.alternatives : []).find((x) => x && x.medId === quote.medId);
    if (a && a.label) return a.label;
  }
  return rxDisplayName(quote);
}

export function prescribedLabel(quote) {
  if (!quote) return 'your medication';
  if (quote.prescribed && quote.prescribed.label) return quote.prescribed.label;
  // Post-#342 `product` follows the ordered item, so once a change is picked
  // the Rx is only safely described by rxName/rxStrength or the strengths row.
  if (isChange(quote)) {
    const s = strengths(quote).find((x) => x && x.medId === rxMedId(quote));
    if (s && s.label) return s.label;
    const rx = [quote.rxName, quote.rxStrength].filter(Boolean).join(' ');
    if (rx) return rx;
  }
  return rxDisplayName(quote);
}

// Every quoted refill shows ONE medication step from the quote (BE #341):
// the current Rx, its other strengths, and its other forms. Never the plan
// picker. Shown whenever there is anything else to ask for.
// us at least one other live strength to ask for. With none, "change" is
// impossible and the step would be noise; the submit still carries
// dose:{choice:'keep'}.
export function needsDoseStep(quote) {
  return Boolean(quote) && (alternativeStrengths(quote).length > 0 || alternatives(quote).length > 0 || Boolean(requestedAtIntake(quote)));
}

export function productName(quote) {
  const p = quote?.product || {};
  return p.name || [p.brand, p.form].filter(Boolean).join(' ') || 'your medication';
}

export function buildRefillQuestions(quote) {
  const out = [];
  if (needsPharmacyStep(quote)) {
    out.push({
      id: REFILL_PHARMACY_ID,
      type: 'refill-pharmacy',
      question: 'Where should we send your prescription?',
      subtitle:
        'Your clinician reviews your refill. The pharmacy fills and bills the medication; Fitpal charges the consultation.',
      required: true,
    });
  }
  if (needsDoseStep(quote)) {
    out.push({
      id: REFILL_DOSE_ID,
      type: 'refill-dose',
      question: alternatives(quote).length
        ? 'Keep your current medication, or ask for a change?'
        : 'Keep your current dose, or ask for a change?',
      subtitle: alternatives(quote).length
        ? "Your clinician decides whether to change your medication. If they don't, your refill is written as it is today."
        : "Your clinician decides whether to change your dose. If they don't, your refill is written at your current strength.",
      required: true,
    });
  }
  return out;
}

// Where the two steps sit: after the clinical questions, before the free-text
// "anything else for the doctor" + consent + checkout tail — so consent stays
// right before payment. Works for every follow-up builder because it keys on
// the tail slide ids rather than on a position.
const TAIL_IDS = ['additional-questions', 'doctor-questions', 'main-consent', 'truthfulness-consent', 'checkout'];

export function insertRefillSteps(flow, quote) {
  const steps = buildRefillQuestions(quote);
  if (!steps.length || !Array.isArray(flow) || !flow.length) return flow;
  let at = flow.findIndex((q) => q && TAIL_IDS.includes(q.id));
  if (at < 0) at = flow.length - 1; // in front of whatever is last (checkout)
  return [...flow.slice(0, at), ...steps, ...flow.slice(at)];
}

// ── Answers ──────────────────────────────────────────────────────────────────
// refill-pharmacy: { choice: 'same' | 'different', pharmacy?: { pharmacyId, storeName, address1, city, state, zip, phone } }
// refill-dose:     { choice: 'keep' | 'change', requestedMedId?, requestedLabel?, reason? }

export function pharmacyStepComplete(quote, answer) {
  if (!answer || !answer.choice) return false;
  if (answer.choice === 'manufacturer') return Boolean(manufacturerOption(quote));
  if (answer.choice === 'same') return hasSamePharmacyOption(quote);
  if (answer.choice === 'different') return Boolean(answer.pharmacy && answer.pharmacy.pharmacyId);
  return false;
}

export function doseStepComplete(answer) {
  if (!answer || !answer.choice) return false;
  if (answer.choice === 'keep') return true;
  if (answer.choice === 'change') {
    return Boolean(answer.requestedMedId) && Boolean((answer.reason || '').trim());
  }
  return false;
}

// The fulfillment route a pharmacy answer implies — the parent re-quotes with
// it so the quote (and its price / currentPharmacy) matches the choice.
// null = no answer yet, leave the quote's route alone.
export function routeForPharmacyAnswer(quote, answer) {
  if (!quote || !answer || !answer.choice) return null;
  if (answer.choice === 'manufacturer') return manufacturerOption(quote) ? ROUTE_MANUFACTURER : null;
  if (answer.choice === 'same' || answer.choice === 'different') {
    return allowedRoutes(quote).includes(ROUTE_LOCAL) ? ROUTE_LOCAL : null;
  }
  return null;
}

// A prior refill sent to the manufacturer defaults the next quote to that leg;
// pre-select the card so the patient confirms rather than re-chooses.
export function defaultPharmacyAnswer(quote) {
  if (isManufacturerLeg(quote) && manufacturerOption(quote)) return { choice: 'manufacturer' };
  return undefined;
}

// What the payment call and the submit carry for the pharmacy (§7: local leg
// only; the BE rejects a local leg without one as 422 pharmacy_required).
export function pharmacySelectionFrom(quote, pharmacyAnswer) {
  if (!isLocalLeg(quote) || !pharmacyAnswer) return null;
  if (pharmacyAnswer.choice === 'same' && hasSamePharmacyOption(quote)) {
    return {
      mode: 'own',
      pharmacyId: String(quote.currentPharmacy.pharmacyId),
      storeName: (pharmacyAnswer.pharmacy && pharmacyAnswer.pharmacy.storeName) || '',
    };
  }
  const p = pharmacyAnswer.pharmacy;
  if (pharmacyAnswer.choice === 'different' && p && p.pharmacyId) {
    return {
      mode: 'own',
      pharmacyId: String(p.pharmacyId),
      storeName: p.storeName || '',
      city: p.city || '',
      state: p.state || '',
      zip: p.zip || '',
    };
  }
  return null;
}

// The dashboard's structured copy of the choice (§3). Compounded / partner
// refills send { choice: 'same' } — the partner pharmacy ships as today.
export function pharmacyBlockFrom(quote, pharmacyAnswer) {
  if (!needsPharmacyStep(quote) || !pharmacyAnswer || !pharmacyAnswer.choice) {
    return { choice: 'same' };
  }
  if (pharmacyAnswer.choice === 'manufacturer') {
    const m = manufacturerOption(quote);
    // The server replaces any pharmacy we send with the manufacturer's id; this
    // block is the dashboard's copy of the choice.
    return m ? { choice: 'manufacturer', name: m.name, manufacturer: m.key, pharmacyId: String(m.pharmacyId) } : { choice: 'same' };
  }
  if (pharmacyAnswer.choice === 'same') return { choice: 'same' };
  const p = pharmacyAnswer.pharmacy || {};
  return {
    choice: 'different',
    name: p.storeName || '',
    address: [p.address1, p.city, p.state, p.zip].filter(Boolean).join(', '),
    phone: p.phone || '',
  };
}

// `kind`: 'strength' (same form, other dose) | 'alternative' (other form).
export function doseBlockFrom(doseAnswer) {
  if (!doseAnswer || doseAnswer.choice !== 'change') return { choice: 'keep' };
  return {
    choice: 'change',
    requestedMedId: doseAnswer.requestedMedId,
    requestedLabel: doseAnswer.requestedLabel || '',
    reason: (doseAnswer.reason || '').trim(),
    ...(doseAnswer.kind ? { kind: doseAnswer.kind } : {}),
  };
}

// The ONLY path by which the dose request reaches Beluga's prescriber (§7).
// Rendered like every other answer: one Q, one A.
export function doseRequestQa(quote, doseAnswer) {
  const cur = currentStrength(quote);
  const block = doseBlockFrom(doseAnswer);
  const isForm = block.kind === 'alternative';
  // A form change names the whole Rx ("Testosterone Cream 100mg/gm → Testosterone
  // Cypionate injection"); a dose change names the strengths.
  const curLabel = isForm ? rxDisplayName(quote) : ((cur && cur.label) || '');
  let answer;
  if (block.choice === 'change') {
    const arrow = `${curLabel || (isForm ? 'current medication' : 'current dose')} → ${block.requestedLabel || block.requestedMedId}`;
    answer = block.reason ? `${arrow} — ${block.reason}` : arrow;
  } else {
    answer = curLabel ? `No change (stay at ${curLabel})` : 'No change';
  }
  return {
    question: isForm ? 'Requested medication change' : 'Requested dose change',
    answer,
    slide_id: REFILL_DOSE_ID,
    rawAnswer: block,
  };
}

// ── Bodies ───────────────────────────────────────────────────────────────────

// POST /payments/square-payment-create-payment, quote path (§7): the BE charges
// the stored quote's totalCents. visitType is the quote's, echoed verbatim —
// casing is not uniform across visit types ('weightlossfollowup' vs
// 'antiAgingFollowup') and the bare 'followup' is not an alias.
export function paymentExtrasFrom(quote, answers = {}) {
  if (!quote || !quote.quoteId) return {};
  const out = {
    quoteId: quote.quoteId,
    visitType: quote.visitType,
    selectedMedIds: [quote.medId],
  };
  if (isConsultLeg(quote)) {
    // Manufacturer leg pays exactly like the local leg (BE #345); no
    // pharmacySelection — the server routes to the manufacturer's id.
    out.prescriptionFulfillment = 'local';
    const ps = pharmacySelectionFrom(quote, answers[REFILL_PHARMACY_ID]);
    if (ps) out.pharmacySelection = ps;
  }
  return out;
}

// POST /followup/submit additions (§3 + §7). `bound` = the payment was made
// against this quote (consult-only leg) → quoteId + visitType ride along so the
// BE can verify payment == quote (409 quote_mismatch auto-refunds otherwise).
export function submitExtrasFrom(quote, answers = {}, { rxId = null, bound = true } = {}) {
  if (!quote) return {};
  const out = {
    fulfillmentRoute: quote.fulfillmentRoute || (isRegularRx(quote) ? ROUTE_LOCAL : ROUTE_PARTNER),
    pharmacy: pharmacyBlockFrom(quote, answers[REFILL_PHARMACY_ID]),
    dose: doseBlockFrom(answers[REFILL_DOSE_ID]),
  };
  if (rxId) out.rxId = rxId;
  if (bound && quote.quoteId) {
    out.quoteId = quote.quoteId;
    out.visitType = quote.visitType;
  }
  const ps = pharmacySelectionFrom(quote, answers[REFILL_PHARMACY_ID]);
  if (ps) out.pharmacySelection = ps;
  return out;
}

// What the dashboard gets back on fitpal:visit-complete (§4).
export function visitCompleteExtrasFrom(quote, answers = {}) {
  if (!quote) return {};
  return {
    pricingBasis: quote.pricingBasis || null,
    amountCents: Number.isFinite(Number(quote.totalCents)) ? Number(quote.totalCents) : null,
    fulfillmentRoute: quote.fulfillmentRoute || null,
    pharmacyChoice: pharmacyBlockFrom(quote, answers[REFILL_PHARMACY_ID]).choice,
    doseChoice: doseBlockFrom(answers[REFILL_DOSE_ID]).choice,
  };
}

// ── Display ──────────────────────────────────────────────────────────────────

export const fmtUsd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

// Checkout lines, from the quote only. consult_only → one consultation line;
// medication → the medication (plus the consult when the BE itemises it).
export function quoteLineItems(quote, doseAnswer) {
  if (!quote) return [];
  const total = Number(quote.totalCents) || 0;
  if (isConsultOnly(quote)) {
    const fee = Number.isFinite(Number(quote.consultFeeCents)) ? Number(quote.consultFeeCents) : total;
    return [{ key: 'consult', label: 'Consultation fee', cents: fee, note: 'Medication is billed by your pharmacy.' }];
  }
  const lines = [];
  const med = Number(quote.medicationCents);
  const fee = Number(quote.consultFeeCents);
  if (Number.isFinite(med) && med > 0) lines.push({ key: 'medication', label: orderedLabel(quote, doseAnswer), cents: med });
  if (Number.isFinite(fee) && fee > 0) lines.push({ key: 'consult', label: 'Consultation fee', cents: fee });
  if (!lines.length) lines.push({ key: 'total', label: orderedLabel(quote, doseAnswer), cents: total });
  return lines;
}

// ── Backend rejections ───────────────────────────────────────────────────────

function errCodes(err) {
  const d = (err && err.response && err.response.data) || {};
  return [d.code, d.errorCode].filter(Boolean).map((c) => String(c).toLowerCase());
}
const errStatus = (err) => (err && err.response && err.response.status) || null;

// Payment call: nothing was charged → re-quote, re-render, confirm again. Never
// retry the charge with the old quoteId.
export function isQuoteStaleError(err) {
  return errStatus(err) === 409 && errCodes(err).includes('quote_stale');
}

// Submit: the visit already exists for this quote (a double submit) — no
// refund, nothing to fix; show the success state.
export function isQuoteAlreadySubmittedError(err) {
  return errStatus(err) === 409 && errCodes(err).includes('quote_already_submitted');
}
